export const RUNTIME_CONFIG_WINDOW_KEY = '__ARETE_RUNTIME_CONFIG__' as const;

export interface RuntimeConfig {
  readonly itemManagementLegacyRoutes: boolean;
  readonly valid: boolean;
}

export interface ExternalRuntimeConfig {
  readonly ITEM_MANAGER_LEGACY_ROUTES_ENABLED?: unknown;
}

interface RuntimeConfigResponse {
  readonly ok: boolean;
  json(): Promise<unknown>;
}

export type RuntimeConfigFetcher = (
  input: string,
  init?: RequestInit,
) => Promise<RuntimeConfigResponse>;

export const DISABLED_RUNTIME_CONFIG: RuntimeConfig = Object.freeze({
  itemManagementLegacyRoutes: false,
  valid: false,
});

type RuntimeConfigWindow = typeof globalThis & {
  [RUNTIME_CONFIG_WINDOW_KEY]?: unknown;
};

export function parseRuntimeConfig(input: unknown): RuntimeConfig {
  if (!input || typeof input !== 'object') {
    return DISABLED_RUNTIME_CONFIG;
  }

  const enabled = (input as ExternalRuntimeConfig).ITEM_MANAGER_LEGACY_ROUTES_ENABLED;
  if (typeof enabled !== 'boolean') {
    return DISABLED_RUNTIME_CONFIG;
  }

  return Object.freeze({
    itemManagementLegacyRoutes: enabled,
    valid: true,
  });
}

export async function loadRuntimeConfig(
  url: string,
  fetcher: RuntimeConfigFetcher = globalThis.fetch.bind(globalThis),
): Promise<RuntimeConfig> {
  try {
    const response = await fetcher(url, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!response.ok) {
      return DISABLED_RUNTIME_CONFIG;
    }
    return parseRuntimeConfig(await response.json());
  } catch {
    return DISABLED_RUNTIME_CONFIG;
  }
}

export function installRuntimeConfig(config: RuntimeConfig): void {
  (globalThis as RuntimeConfigWindow)[RUNTIME_CONFIG_WINDOW_KEY] = Object.freeze({
    itemManagementLegacyRoutes: config.valid && config.itemManagementLegacyRoutes,
    valid: config.valid,
  });
}

export function readInstalledRuntimeConfig(): RuntimeConfig {
  const installed = (globalThis as RuntimeConfigWindow)[RUNTIME_CONFIG_WINDOW_KEY];
  if (!installed || typeof installed !== 'object') {
    return DISABLED_RUNTIME_CONFIG;
  }

  const candidate = installed as Partial<RuntimeConfig>;
  if (
    typeof candidate.itemManagementLegacyRoutes !== 'boolean' ||
    typeof candidate.valid !== 'boolean' ||
    !candidate.valid
  ) {
    return DISABLED_RUNTIME_CONFIG;
  }

  return Object.freeze({
    itemManagementLegacyRoutes: candidate.itemManagementLegacyRoutes,
    valid: true,
  });
}
